import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import axios from 'axios';
import { 
  FaUser, 
  FaArrowUp, 
  FaArrowDown, 
  FaClock,
  FaExternalLinkAlt,
  FaChartLine,
  FaCalendarAlt,
  FaFilter,
  FaInfoCircle
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import '../styles/Blank.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Modern chart configuration with sleek styling
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { 
      display: false 
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      titleFont: { 
        size: 14, 
        weight: 'bold',
        family: "'Inter', sans-serif" 
      },
      bodyFont: { 
        size: 12,
        family: "'Inter', sans-serif" 
      },
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      callbacks: {
        label: (context) => {
          return `Records: ${context.parsed.y.toLocaleString()}`;
        }
      }
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  scales: {
    x: { 
      grid: { 
        display: false,
        drawBorder: false
      },
      ticks: { 
        color: '#94a3b8',
        font: {
          family: "'Inter', sans-serif",
          size: 11
        }
      }
    },
    y: { 
      display: true, 
      beginAtZero: true,
      grid: { 
        color: 'rgba(226, 232, 240, 0.1)',
        drawBorder: false
      },
      ticks: { 
        color: '#94a3b8',
        font: {
          family: "'Inter', sans-serif",
          size: 11
        },
        padding: 8,
        callback: (value) => value.toLocaleString()
      },
    },
  },
  elements: {
    line: {
      tension: 0.3,
      borderWidth: 2,
      fill: true,
    },
    point: {
      radius: 2,
      hoverRadius: 5,
      hitRadius: 10,
    }
  },
};

// Unified backend URL
const BACKEND_URL = 'https://chaman.gob.pk/eac_backend/unified_analytics.php';

// Enhanced fetch function with better error handling
const fetchRecords = async (type, dateRange = null) => {
  try {
    // If date range is provided, get range data
    if (dateRange && type !== 'users') {
      const rangePayload = {
        action: 'get_chart_data',
        type: type,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      };
      
      const rangeResponse = await axios.post(BACKEND_URL, rangePayload, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (rangeResponse.data && typeof rangeResponse.data === 'object') {
        return {
          total: rangeResponse.data.total || 0,
          todayCount: 0,
          dailyData: rangeResponse.data.daily_data || []
        };
      }
      
      return {
        total: parseInt(rangeResponse.data || 0, 10),
        todayCount: 0,
        dailyData: []
      };
    }
    
    // Regular fetch without date range
    const payload = {
      action: 'get_records',
      type: type
    };

    const totalResponse = await axios.post(BACKEND_URL, payload, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const total = parseInt(totalResponse.data || 0, 10);
    
    // For users, don't fetch today count
    if (type === 'users') {
      return {
        total: Number.isNaN(total) ? 0 : total,
        todayCount: 0,
        dailyData: []
      };
    }
    
    // Fetch today's records for other types
    const todayPayload = {
      action: 'get_today_records',
      type: type
    };
    
    const todayResponse = await axios.post(BACKEND_URL, todayPayload, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const todayCount = parseInt(todayResponse.data || 0, 10);
    
    return {
      total: Number.isNaN(total) ? 0 : total,
      todayCount: Number.isNaN(todayCount) ? 0 : todayCount,
      dailyData: []
    };
  } catch (error) {
    console.error(`Failed to fetch records for ${type}:`, error.message || error);
    return { 
      total: 0, 
      todayCount: 0,
      dailyData: []
    };
  }
};

// Generate chart data based on record count and date range
const generateChartData = (totalRecords, type, dailyData = [], dateRange = null) => {
  // Color schemes
  const colorSchemes = {
    'local_certificates': '#6366f1',
    'local_domicile': '#10b981',
    'birth_certificates': '#f59e0b',
    'death_certificates': '#64748b',
    'marriage_certificates': '#8b5cf6',
    'general_dsr': '#ec4899',
    'special_dsr': '#f97316',
    'fir': '#ef4444',
    'passport': '#8b5cf6',
    'arms_license': '#f59e0b',
    'mutation': '#10b981',
    'fard': '#6366f1',
    'complaints': '#ec4899',
    'users': '#3b82f6',
    'default': '#3b82f6',
  };

  // If we have daily data from date range, use it
  if (dailyData && dailyData.length > 0) {
    const labels = dailyData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const dataPoints = dailyData.map(item => item.count);
    
    return {
      labels: labels,
      datasets: [{
        label: type,
        data: dataPoints,
        backgroundColor: `${colorSchemes[type] || colorSchemes.default}20`,
        borderColor: colorSchemes[type] || colorSchemes.default,
        borderWidth: 2,
        pointBackgroundColor: colorSchemes[type] || colorSchemes.default,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        fill: {
          target: 'origin',
          above: `${colorSchemes[type] || colorSchemes.default}20`,
        },
        tension: 0.3,
      }]
    };
  }

  // For users with date range but no daily data, show simple bar
  if (type === 'users' && dateRange) {
    return {
      labels: ['Total Users'],
      datasets: [{
        label: type,
        data: [totalRecords],
        backgroundColor: `${colorSchemes[type] || colorSchemes.default}40`,
        borderColor: colorSchemes[type] || colorSchemes.default,
        borderWidth: 2,
        fill: true,
      }]
    };
  }

  // Original logic for grouped data (when no date range or no daily data)
  let groupSize, maxGroups;
  if (totalRecords > 2000) {
    groupSize = 400;
    maxGroups = 20;
  } else if (totalRecords > 500) {
    groupSize = 100;
    maxGroups = 20;
  } else if (totalRecords > 100) {
    groupSize = 25;
    maxGroups = 20;
  } else if (totalRecords > 50) {
    groupSize = 10;
    maxGroups = 15;
  } else {
    groupSize = 5;
    maxGroups = 10;
  }

  let groupCount = Math.ceil(totalRecords / groupSize);
  if (groupCount > maxGroups) {
    groupCount = maxGroups;
    groupSize = Math.ceil(totalRecords / maxGroups);
  }

  const labels = Array.from({ length: groupCount }, (_, i) => `${i+1}`);
  const dataPoints = Array(groupCount).fill(0).map((_, i) => {
    let baseValue = groupSize;
    
    if (i === groupCount - 1) {
      const remaining = totalRecords - (i * groupSize);
      baseValue = remaining > 0 ? remaining : groupSize;
    }
    
    const variation = groupSize > 50 ? 
      (0.9 + Math.random() * 0.2) :
      (0.7 + Math.random() * 0.6);
      
    return Math.floor(baseValue * variation);
  });

  const sum = dataPoints.reduce((a, b) => a + b, 0);
  if (sum !== totalRecords) {
    dataPoints[dataPoints.length - 1] += totalRecords - sum;
  }

  return {
    labels: labels,
    datasets: [{
      label: type,
      data: dataPoints,
      backgroundColor: `${colorSchemes[type] || colorSchemes.default}20`,
      borderColor: colorSchemes[type] || colorSchemes.default,
      borderWidth: 2,
      pointBackgroundColor: colorSchemes[type] || colorSchemes.default,
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      fill: {
        target: 'origin',
        above: `${colorSchemes[type] || colorSchemes.default}20`,
      },
      tension: 0.3,
    }]
  };
};

// Date Range Filter Component
const DateRangeFilter = ({ onDateRangeChange, isFiltering }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleApplyFilter = () => {
    if (startDate && endDate) {
      onDateRangeChange({ startDate, endDate });
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    onDateRangeChange(null);
  };

  // Set default date range to last 30 days
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  return (
    <motion.div 
      className="date-range-filter"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="filter-header">
        <FaCalendarAlt className="filter-icon" />
        <h3>Filter by Date Range</h3>
        <div className="filter-info">
          <FaInfoCircle size={14} />
          <span>Users data shows total count (no date filter)</span>
        </div>
      </div>
      
      <div className="date-inputs">
        <div className="date-input-group">
          <label>From Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />
        </div>
        
        <div className="date-input-group">
          <label>To Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />
        </div>
      </div>
      
      <div className="filter-actions">
        <button 
          onClick={handleApplyFilter}
          disabled={!startDate || !endDate || isFiltering}
          className="apply-filter-btn"
        >
          <FaFilter size={14} />
          {isFiltering ? 'Applying...' : 'Apply Filter'}
        </button>
        
        <button 
          onClick={handleClearFilter}
          className="clear-filter-btn"
        >
          Clear Filter
        </button>
      </div>
    </motion.div>
  );
};

// Animated record box component with modern styling
const RecordBox = ({ title, type, routePath, dateRange, isFiltering }) => {
  const [data, setData] = useState({});
  const [total, setTotal] = useState(null);
  const [todayCount, setTodayCount] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await fetchRecords(type, dateRange);
        
        setTotal(result.total);
        setTodayCount(result.todayCount);
        setDailyData(result.dailyData || []);
        setLastUpdate(new Date());
        
        // Generate chart data
        const chartData = generateChartData(
          result.total, 
          type, 
          result.dailyData || [], 
          dateRange
        );
        setData(chartData);
      } catch (err) {
        console.error(`Error loading data for ${title}`, err);
        setTotal(0);
        setTodayCount(0);
        setDailyData([]);
      }
    };

    loadData();
    
    // Set up refresh interval (every 5 minutes)
    const intervalId = setInterval(() => {
      loadData();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [type, title, dateRange]);

  const handleClick = () => {
    if (routePath) {
      navigate(routePath);
    }
  };

  const isLoading = total === null;
  const isActive = todayCount >= 5;
  const activityClass = isActive ? 'active' : 'inactive';
  const formattedTotal = total !== null ? total.toLocaleString() : '...';

  // Format title for display
  const displayTitle = title.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  const isUsers = type === 'users';

  return (
    <motion.div
      className={`analytics-card ${isLoading ? 'loading' : ''} ${dateRange ? 'filtered' : ''} ${isUsers ? 'users-card' : ''}`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ 
        y: -5, 
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
        transition: { duration: 0.2, ease: 'easeOut' }
      }}
    >
      <div className="card-header">
        <h3 className="card-title">
          {displayTitle}
          {isUsers && dateRange && (
            <span className="no-date-filter-badge" title="Shows total count (no date filter)">
              <FaInfoCircle size={10} />
            </span>
          )}
          <motion.span 
            className="external-icon"
            animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : -5 }}
            transition={{ duration: 0.2 }}
          >
            <FaExternalLinkAlt size={12} />
          </motion.span>
        </h3>
        
        <div className="total-count">
          {formattedTotal}
          <span className="count-label">
            {dateRange && !isUsers ? 'Filtered' : 'Total'}
          </span>
        </div>
      </div>

      {!isLoading && !dateRange && !isUsers && (
        <div className={`activity-indicator ${activityClass}`}>
          <div className="activity-content">
            <FaUser className="user-icon" />
            {isActive ? (
              <FaArrowUp className="arrow-icon" />
            ) : (
              <FaArrowDown className="arrow-icon" />
            )}
            <span className="activity-text">
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          {lastUpdate && (
            <div className="update-time">
              <FaClock className="clock-icon" />
              <span>Updated: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
        </div>
      )}

      {dateRange && !isUsers && (
        <div className="date-range-indicator">
          <FaCalendarAlt size={12} />
          <span>
            {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
          </span>
        </div>
      )}

      {isUsers && dateRange && (
        <div className="no-date-filter-indicator">
          <FaInfoCircle size={12} />
          <span>Total users (date filter not applicable)</span>
        </div>
      )}

      <div className="chart-container">
        {Object.keys(data).length > 0 ? (
          <Line
            data={data} 
            options={chartOptions}
            redraw={false}
          />
        ) : (
          <div className="empty-chart">
            <div className="empty-chart-message">
              {total === 0 ? 'No data available' : 'Loading chart...'}
            </div>
            {total === 0 && (
              <div className="empty-chart-icon">
                <FaChartLine size={32} />
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Main dashboard component with modern layout
const Blank = () => {
  const [dateRange, setDateRange] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);

  const handleDateRangeChange = async (newDateRange) => {
    if (newDateRange) {
      setIsFiltering(true);
      setDateRange(newDateRange);
      // Small delay to show loading state
      setTimeout(() => setIsFiltering(false), 500);
    } else {
      setDateRange(null);
    }
  };

  const recordConfigs = [
    {
      title: 'local_certificates',
      type: 'local_certificates',
      routePath: '/dashboard/local-certificate-list',
    },
    {
      title: 'birth_certificates',
      type: 'birth_certificates',
      routePath: '/dashboard/birth-certificate-list',
    },
    {
      title: 'death_certificates',
      type: 'death_certificates',
      routePath: '/dashboard/death-certificate-list',
    },
    {
      title: 'marriage_certificates',
      type: 'marriage_certificates',
      routePath: '/dashboard/marriage-certificate-list',
    },
    {
      title: 'general_dsr',
      type: 'general_dsr',
      routePath: '/dashboard/gdsr-list',
    },
    {
      title: 'special_dsr',
      type: 'special_dsr',
      routePath: '/dashboard/special-dsr-list',
    },
    {
      title: 'passport',
      type: 'passport',
      routePath: '/dashboard/passport-list',
    },
    {
      title: 'arms_license',
      type: 'arms_license',
      routePath: '/dashboard/arms-license-list',
    },
    {
      title: 'mutation',
      type: 'mutation',
      routePath: '/dashboard/mutation-list',
    },
    {
      title: 'fard',
      type: 'fard',
      routePath: '/dashboard/fard-list',
    },
    {
      title: 'fir',
      type: 'fir',
      routePath: '/dashboard/fir-list',
    },
    {
      title: 'local_domicile',
      type: 'local_domicile',
      routePath: '/dashboard/local-domicile-list',
    },
    {
      title: 'complaints',
      type: 'complaints',
      routePath: '/dashboard/complaints-cell',
    },
    {
      title: 'users',
      type: 'users',
      routePath: '/dashboard/users-list',
    },
  ];

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-controls">
        <DateRangeFilter 
          onDateRangeChange={handleDateRangeChange}
          isFiltering={isFiltering}
        />
      </div>
      
      <motion.div 
        className="dashboard-grid"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        {recordConfigs.map((config, index) => (
          <RecordBox 
            key={index} 
            title={config.title} 
            type={config.type}
            routePath={config.routePath} 
            dateRange={dateRange}
            isFiltering={isFiltering}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default Blank;